import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, Sparkles, Search } from "lucide-react";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { QuizEditorDialog } from "./QuizEditorDialog";

const STATUS = {
  none: ["No quiz", "secondary"],
  draft: ["Draft: needs review", "outline"],
  published: ["Live", "default"],
};

export default function AdminQuizzes() {
  const { token } = useAdminAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/admin/quizzes`, { headers }).then((r) => setData(r.data)).catch(() => toast.error("Could not load quizzes"));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!data?.batch?.running) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.batch?.running]);

  const draftMissing = async () => {
    const r = await axios.post(`${API}/admin/quizzes/generate-missing`, {}, { headers });
    const b = r.data;
    toast.success(
      b.total
        ? `Drafting ${b.total} quizzes in the background…`
        : b.needs_source
          ? `No drafts started: ${b.needs_source} songs need lyrics or a Lead Sheet first.`
          : "Every song already has a quiz."
    );
    load();
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  const songs = data.songs.filter((s) => `${s.course_name} ${s.title}`.toLowerCase().includes(q.toLowerCase()));
  const counts = data.songs.reduce((a, s) => ({ ...a, [s.quiz_status]: (a[s.quiz_status] || 0) + 1 }), {});

  return (
    <div className="space-y-6" data-testid="admin-quizzes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="h-8 w-8 text-gold" />
          <div>
            <h1 className="font-serif text-2xl font-bold">Song Quizzes</h1>
            <p className="text-sm text-muted-foreground" data-testid="admin-quizzes-summary">
              {counts.published || 0} live · {counts.draft || 0} drafts to review · {counts.none || 0} without a quiz · {data.passes}/{data.attempts} attempts passed
            </p>
          </div>
        </div>
        <Button onClick={draftMissing} disabled={data.batch.running} data-testid="admin-quizzes-draft-missing-btn">
          {data.batch.running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          {data.batch.running ? `Drafting ${data.batch.done}/${data.batch.total}…` : "Draft all missing quizzes with AI"}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        AI drafts 5 questions from each song's lyrics and Lead Sheet. Nothing reaches students until you review a draft and click Publish.
        Students take a quiz after listening to the whole song; 4 of 5 correct unlocks that song's Lead Sheet.
      </p>
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search songs" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-quizzes-search" />
      </div>
      <Card>
        <CardContent className="divide-y p-0">
          {songs.map((s) => {
            const [label, variant] = STATUS[s.quiz_status];
            return (
              <div key={s.content_id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`admin-quiz-row-${s.content_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{s.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.course_name}{s.has_lyrics ? " · lyrics" : " · no lyrics"}{s.has_lead_sheet ? " · Lead Sheet" : ""}
                  </p>
                </div>
                <Badge variant={variant} data-testid={`admin-quiz-status-${s.content_id}`}>{label}</Badge>
                <Button size="sm" variant={s.quiz_status === "draft" ? "default" : "outline"} onClick={() => setEditing(s)} data-testid={`admin-quiz-edit-${s.content_id}`}>
                  {s.quiz_status === "none" ? "Create" : s.quiz_status === "draft" ? "Review" : "Edit"}
                </Button>
              </div>
            );
          })}
          {songs.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No uploaded songs match.</p>}
        </CardContent>
      </Card>
      <QuizEditorDialog song={editing} token={token} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
